export const DASHBOARD_ACTIONS = [
  {
    id: "ai_post",
    label: "AI Post",
    description: "Generate platform-ready posts with the agent workflow.",
    to: "/create",
    primary: true,
  },
  {
    id: "manual_post",
    label: "Manual Post",
    description: "Write and schedule a post without running the agent.",
    to: "/manual-post",
  },
  {
    id: "repurpose",
    label: "Repurpose",
    description: "Turn existing content, links, and drafts into new posts.",
    to: "/repurpose",
  },
  {
    id: "write_blog",
    label: "Write Blog",
    description: "Draft long-form content that can feed your social engine.",
    to: "/blog",
  },
  {
    id: "import",
    label: "Import",
    description: "Bring in external content, webhook items, and source material.",
    to: "/inbox",
  },
  {
    id: "calendar",
    label: "Calendar",
    description: "Review scheduled content and publishing coverage.",
    to: "/calendar",
  },
];

const byDateDesc = (field) => (a, b) => new Date(b?.[field] || 0) - new Date(a?.[field] || 0);
const byDateAsc = (field) => (a, b) => new Date(a?.[field] || 0) - new Date(b?.[field] || 0);
const projectDateDesc = (a, b) => new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0);

export function getDashboardActions() {
  return DASHBOARD_ACTIONS.map((action) => ({ ...action }));
}

function buildPrimaryFocus({ activeRuns, reviewQueue, openInbox, projectsNeedingReview, upcomingCalendar }) {
  if (activeRuns.length > 0) {
    const run = activeRuns[0];
    return {
      id: "active_run",
      kind: "run",
      title: "Agent run in progress",
      description: run.title || run.name || "An agent workflow is still moving.",
      to: `/runs/${run.id}`,
      cta: "Open run",
    };
  }

  if (reviewQueue.length > 0) {
    const asset = reviewQueue[0];
    return {
      id: "review_queue",
      kind: "review",
      title: "Review generated content",
      description: asset.title || "A generated post needs your decision.",
      to: `/library/${asset.id}`,
      cta: "Review post",
    };
  }

  if (openInbox.length > 0) {
    const event = openInbox[0];
    return {
      id: "inbox_triage",
      kind: "inbox",
      title: "Triage imported source material",
      description: event.title || "An imported item is waiting in the inbox.",
      to: "/inbox",
      cta: "Open inbox",
    };
  }

  if (projectsNeedingReview.length > 0) {
    const project = projectsNeedingReview[0];
    return {
      id: "project_review",
      kind: "project",
      title: "Move a campaign forward",
      description: project.title || "A project needs attention.",
      to: `/projects/${project.id}`,
      cta: "Open project",
    };
  }

  if (upcomingCalendar.length > 0) {
    const item = upcomingCalendar[0];
    return {
      id: "calendar_check",
      kind: "calendar",
      title: "Check upcoming scheduled content",
      description: item.title || "Your publishing queue has upcoming posts.",
      to: "/calendar",
      cta: "Open calendar",
    };
  }

  return {
    id: "create_next",
    kind: "create",
    title: "Create the next post",
    description: "Start with the AI agent and turn an idea into platform-ready content.",
    to: "/create",
    cta: "Start creating",
  };
}

function buildAttentionQueue({ activeRuns, reviewQueue, openInbox, projectsNeedingReview, upcomingCalendar }) {
  return [
    ...activeRuns.slice(0, 1).map((run) => ({
      id: "active_run",
      kind: "run",
      title: "Agent run in progress",
      description: run.title || run.name || "Agent workflow is active.",
      to: `/runs/${run.id}`,
      at: run.started_at,
      atLabel: "Started",
      platforms: run.platforms,
    })),
    ...reviewQueue.slice(0, 1).map((asset) => ({
      id: `review_${asset.id}`,
      kind: "review",
      assetId: asset.id,
      title: asset.title || "Generated post needs review",
      description: (asset.hook || asset.body || "").trim() || "A generated post is waiting for your approval.",
      to: `/library/${asset.id}`,
      at: asset.updated_at,
      atLabel: "Updated",
      platform: asset.platform,
      status: asset.status,
      charCount: Number.isFinite(asset.char_count) ? asset.char_count : null,
      charLimit: asset.char_limit || null,
    })),
    ...openInbox.slice(0, 1).map((event) => ({
      id: `inbox_${event.id}`,
      kind: "inbox",
      title: event.title || "Inbox item",
      description: event.source ? `Imported from ${event.source}` : "Imported source material",
      to: "/inbox",
      at: event.received_at,
      atLabel: "Received",
      platform: event.platform,
    })),
    ...projectsNeedingReview.slice(0, 1).map((project) => ({
      id: `project_${project.id}`,
      kind: "project",
      title: project.title || "Project needs review",
      description: "Campaign hub needs a decision",
      to: `/projects/${project.id}`,
      at: project.updated_at,
      atLabel: "Updated",
    })),
    ...upcomingCalendar.slice(0, 1).map((item) => ({
      id: `calendar_${item.id}`,
      kind: "calendar",
      title: item.title || "Scheduled post",
      description: "Next in your publishing queue.",
      to: "/calendar",
      at: item.scheduled_at,
      atLabel: "Publishes",
      atUpcoming: true,
      platform: item.platform,
      status: item.status,
    })),
  ].slice(0, 5);
}

export function buildDashboardWorkspace(state = {}) {
  const assets = Array.isArray(state.assets) ? state.assets : [];
  const runs = Array.isArray(state.runs) ? state.runs : [];
  const calendar = Array.isArray(state.calendar) ? state.calendar : [];
  const inbox = Array.isArray(state.inbox) ? state.inbox : [];
  const blogs = Array.isArray(state.blogs) ? state.blogs : [];
  const createTemplates = Array.isArray(state.createTemplates) ? state.createTemplates : [];
  const projects = Array.isArray(state.projects) ? state.projects : [];

  const reviewQueue = assets
    .filter((asset) => asset.status === "needs_review")
    .sort(byDateDesc("updated_at"));

  const scheduledAssets = assets.filter((asset) => asset.status === "scheduled");
  const scheduledCalendar = calendar
    .filter((item) => item.status === "scheduled")
    .sort(byDateAsc("scheduled_at"));

  const activeRuns = runs
    .filter((run) => ["running", "waiting_for_clarification"].includes(run.status))
    .sort(byDateDesc("started_at"));

  const openInbox = inbox
    .filter((event) => event.status !== "archived")
    .sort(byDateDesc("received_at"));

  const draftBlogs = blogs
    .filter((blog) => blog.status === "draft")
    .sort(byDateDesc("updated_at"));

  const activeProjects = projects
    .filter((project) => project.status !== "archived")
    .sort(projectDateDesc);

  const projectsNeedingReview = projects
    .filter((project) => project.status === "needs_review")
    .sort(projectDateDesc);

  const projectHubs = activeProjects.slice(0, 3);
  const primaryFocus = buildPrimaryFocus({ activeRuns, reviewQueue, openInbox, projectsNeedingReview, upcomingCalendar: scheduledCalendar });
  const attentionQueue = buildAttentionQueue({ activeRuns, reviewQueue, openInbox, projectsNeedingReview, upcomingCalendar: scheduledCalendar });

  return {
    metrics: {
      reviewQueue: reviewQueue.length,
      scheduled: scheduledCalendar.length || scheduledAssets.length,
      activeRuns: activeRuns.length,
      inboxOpen: openInbox.length,
      draftBlogs: draftBlogs.length,
      templates: createTemplates.length,
      projectsActive: activeProjects.length,
      projectsNeedingReview: projectsNeedingReview.length,
    },
    primaryFocus,
    attentionQueue,
    reviewQueue: reviewQueue.slice(0, 5),
    recentRuns: [...runs].sort(byDateDesc("started_at")).slice(0, 4),
    activeRuns: activeRuns.slice(0, 3),
    upcomingCalendar: scheduledCalendar.slice(0, 5),
    openInbox: openInbox.slice(0, 4),
    draftBlogs: draftBlogs.slice(0, 4),
    recentAssets: [...assets].sort(byDateDesc("updated_at")).slice(0, 4),
    templates: [...createTemplates].sort(byDateDesc("updatedAt")).slice(0, 4),
    projectHubs,
  };
}
